import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Tailwind-like class variants for `<e-input-upload>` drop zone mode.
 * Button mode delegates to `<e-button>` and does not use these classes.
 */
export const inputUploadClasses = defineVariants({
  base: `
    flex items-center justify-center
    border border-dashed
    min-h-40 p-6
    cursor-pointer
    outline-none
    transition-all duration-200 ease-out
  `,
  text: `
    flex-1
    min-w-0
    truncate
    text-inherit
    font-inherit
  `,
  placeholder: `text-(--color-foreground)/50`,
  fileInput: `sr-only`,
  dropzoneContent: `flex flex-col items-center justify-center w-full gap-3`,
  dropzoneIcon: `text-(--color-foreground)/60 transition-all duration-200`,
  dropzoneIconActive: `text-(--color-secondary) animate-bounce`,
  dropzoneDescription: `text-sm text-(--color-foreground)/50`,
  fileList: `flex flex-col gap-2 mt-3`,
  fileCard: `flex items-center gap-3 p-2`,
  filePreview: `w-10 h-10 rounded object-cover flex-shrink-0`,
  fileIcon: `w-10 h-10 flex items-center justify-center flex-shrink-0 text-(--color-foreground)/40`,
  fileInfo: `flex-1 min-w-0`,
  fileName: `text-sm truncate text-(--color-foreground)`,
  fileMeta: `text-xs text-(--color-foreground)/50`,
  fileRemove: `flex-shrink-0 p-1 rounded hover:bg-(--color-foreground)/10 cursor-pointer text-(--color-foreground)/40 hover:text-(--color-foreground)`,

  variants: {
    color: {
      primary: `[--input-color:var(--color-primary)] focus-within:ring-primary/30`,
      secondary: `[--input-color:var(--color-secondary)] focus-within:ring-secondary/30`,
      accent: `[--input-color:var(--color-accent)] focus-within:ring-accent/30`,
      error: `[--input-color:var(--color-error)] focus-within:ring-error/30`,
      warning: `[--input-color:var(--color-warning)] focus-within:ring-warning/30`,
      info: `[--input-color:var(--color-info)] focus-within:ring-info/30`,
      success: `[--input-color:var(--color-success)] focus-within:ring-success/30`,
      neutral: `[--input-color:var(--color-neutral)] focus-within:ring-neutral/30`,
      mono: `[--input-color:var(--color-foreground)] focus-within:ring-elevation-200/30`,
    },

    variant: {
      solid: `
        bg-(--input-color) text-(--color-white)
        border-transparent
        hover:bg-(--input-color)/75
      `,
      outline: `
        bg-transparent text-(--color-foreground)
        border-(--input-color)
        hover:bg-(--input-color)/5
      `,
      soft: `
        relative isolate backdrop-blur-lg
        bg-(--input-color)/10 text-(--color-foreground) border-transparent
        hover:bg-(--input-color)/15

        before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-(--color-background)/50
        after:absolute after:inset-0 after:-z-10 after:rounded-[inherit] after:bg-(--input-color)/10
      `,
      subtle: `
        relative isolate backdrop-blur-lg
        text-(--color-foreground) border-(--input-color)/35 hover:bg-(--input-color)/25

        before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-(--color-background)/50
        after:absolute after:inset-0 after:-z-10 after:rounded-[inherit] after:bg-(--input-color)/15
      `,
    },

    size: {
      xs: `px-2 py-1 text-xs rounded`,
      sm: `px-2.5 py-1.5 text-sm rounded-md`,
      md: `px-3 py-2 text-base rounded-lg`,
      lg: `px-4 py-2.5 text-lg rounded-lg`,
      xl: `px-5 py-3 text-xl rounded-lg`,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: `focus-within:ring-2`,
    },

    block: {
      true: `w-full`,
      false: ``,
    },

    dragActive: {
      true: `[--input-color:var(--color-secondary)] bg-(--input-color)/10 border-(--input-color) border-2 scale-[1.02] shadow-lg shadow-(--color-secondary)/20 animate-pulse`,
      false: ``,
    },
  },

  compoundVariants: [
    {
      color: "mono",
      variant: ["solid", "outline"],
      class:
        "focus-within:ring-0! focus-within:outline-1 focus-within:outline-solid focus-within:outline-(--color-foreground) focus-within:outline-offset-4",
    },
  ],

  defaults: {
    color: "neutral",
    variant: "subtle",
    size: "md",
    disabled: false,
    block: false,
    dragActive: false,
  },
});
